import SinglePageExporter from './SinglePageExporter.js';

/**
 * @module Grid/feature/export/exporter/SinglePageUnscaledExporter
 */

/**
 * A single page exporter. Used by the {@link Grid.feature.export.PdfExport} feature to export to single page. Content
 * is exported with dimensions required to fit all requested rows and columns. This allows to generate PDF page not
 * constrained by the standard paper formats.
 *
 * You do not need to use this class directly.
 *
 * ### Extending exporter
 *
 * ```javascript
 * class MySinglePageUnscaledExporter extends SinglePageUnscaledExporter {
 *     // type is required for exporter
 *     static get type() {
 *         return 'mysinglepageunscaledexporter';
 *     }
 *
 *     get stylesheets() {
 *         const stylesheets = super.stylesheets;
 *
 *         stylesheets.forEach(styleNodeOrLinkTag => doSmth(styleNodeOrLinkTag))
 *
 *         return stylesheets;
 *     }
 * }
 *
 * const grid = new Grid({
 *     features : {
 *         pdfExport : {
 *             // this export feature is configured with only one exporter
 *             exporters : [MySinglePageUnscaledExporter]
 *         }
 *     }
 * });
 *
 * // run export with the new exporter
 * grid.features.pdfExport.export({ exporter : 'mysinglepageunscaledexporter' });
 * ```
 *
 * @classtype singlepageunscaled
 * @extends Grid/feature/export/exporter/SinglePageExporter
 */
export default class SinglePageUnscaledExporter extends SinglePageExporter  {
    static $name = 'SinglePageUnscaledExporter';

    static type = 'singlepageunscaled';

    static get title() {
        // In case locale is missing exporter is still distinguishable
        return this.localize('L{singlepageunscaled}');
    }
}

// HACK: terser/obfuscator doesn't yet support async generators, when processing code it converts async generator to regular async
// function.
SinglePageUnscaledExporter.prototype.pagesExtractor = async function * pagesExtractor(config) {
    // When we prepared grid we stretched it horizontally, now we need to gather all rows
    // There are two ways:
    // 1. set component height to scrollable.scrollHeight value to render all rows at once (maybe a bit more complex
    // if rows have variable height)
    // 2. iterate over rows, scrolling new portion into view once in a while
    // #1 sounds simpler, but that might require too much rendering, let's scroll rows instead

    const
        me             = this,
        { client }     = config,
        { totalWidth } = me.exportMeta,
        styles         = me.stylesheets;

    let totalHeight, header, footer;

    totalHeight = await me.renderRows(config) + client.headerHeight + client.footerHeight;

    const html = me.buildPageHtml(config);

    const totalClientHeight = totalHeight;

    // Measure header and footer height
    if (config.headerTpl) {
        header = me.prepareHTML(config.headerTpl({ totalWidth }));
        const height = me.measureElement(header, 'b-export-header');
        totalHeight += height;
    }

    if (config.footerTpl) {
        footer = me.prepareHTML(config.footerTpl({ totalWidth }));
        const height = me.measureElement(footer, 'b-export-footer');
        totalHeight += height;
    }

    config.paperFormat = `${totalWidth}*${totalHeight}`;
    config.orientation = undefined;

    styles.push(
        `<style>
        html,
        .b-print-root body {
            overflow: auto !important;
        }
        
        body {
            height: ${totalHeight}px !important;
            width: ${totalWidth}px !important;
        }
        
        .b-page-wrap {
            direction: ${client.rtl ? 'rtl' : 'ltr'};
        }
         
        #${client.id} {
            height: ${totalClientHeight}px !important;
            width: ${totalWidth}px !important;
        }
        </style>`
    );

    // This is a single page exporter so we only yield one page
    yield {
        html : me.pageTpl({
            html,
            header,
            footer,
            styles,
            currentPage : 0
        })
    };
};
